import React, { useEffect, useRef, useState } from 'react';

/**
 * Example goals per auto-router suite. Clicking a chip fills the textarea —
 * these are seeded from each suite's member tools so the examples map onto
 * analyses the router can actually pick.
 */
const GOAL_EXAMPLES: Record<string, string[]> = {
  cyber_auto: [
    'Compare the prior policy to the renewal',
    'Will ransomware coverage actually pay?',
    'Review war and systemic risk exclusions',
    'Benchmark this policy against the market',
  ],
  do_auto: [
    'Compare the expiring D&O program to the renewal',
    'Benchmark these D&O limits and retentions',
    'Check this policy for financial distress exposure',
    'Look for emerging exposures the policy misses',
  ],
  home_auto: [
    'Benchmark this homeowners policy',
    'Compare these homeowners quotes',
    'What changed at renewal versus the expiring policy?',
    'Review coverage on this rental / dwelling fire property',
  ],
  plines_auto: [
    'Benchmark this personal auto policy',
    'Compare these auto quotes side by side',
    'What changed on the umbrella at renewal?',
    'Benchmark this personal umbrella policy',
  ],
  rt_auto: [
    'Review the insurance requirements in this contract',
    'Check whether our current coverage meets these requirements',
    'Did they give us what we required?',
  ],
  premdev_auto: [
    "Break down what's driving this workers' comp premium",
    'Explain the general liability premium build-up',
    'Decompose this property premium',
    'Break down the package / BOP premium',
  ],
  lossrun_auto: [
    'Summarize the loss history',
    'Build a workers comp underwriter presentation',
  ],
  re_auto: [
    'Extract the key terms from this property policy',
    "Analyze risk transfer in this construction contract",
    "Pull the financials off this builder's risk submission",
    'Compile a portfolio board brief',
  ],
  cl_auto: [
    'Benchmark this commercial package policy',
    'Compare these BOP quotes',
    'What changed on this GL renewal?',
    'Review the action-over exposure on this NY construction program',
  ],
  ca_auto: [
    'Does this policy cover this claim?',
    'Which of these policies responds to this loss?',
    'Audit this carrier denial letter',
  ],
  ntr_auto: [
    'Build the notice-trigger register for this GL policy',
    'What has to be reported, and by when, on this D&O program?',
    'Build the notice-trigger reference for this crime policy',
  ],
};

interface RouterGoalStepProps {
  /** The `*_auto` workflow type being launched */
  workflowType: string;
  /**
   * Same contract as AdditionalContextStep: called on every change so the
   * parent's workflowData.additionalContext stays current without extra state.
   */
  onContextSubmit: (
    context: string,
    pastDocuments?: any[],
    pastReports?: any[],
    additionalFiles?: any[]
  ) => void;
  /** Advance the wizard without a goal — routing falls back to document signature */
  onSkip: () => void;
  initialValue?: string;
}

/**
 * Goal step for the `*_auto` router tools.
 *
 * The router already weighs the typed goal above the document signature; this
 * step exists so the question is actually asked rather than buried in a
 * generic "Add Additional Context" form. No past-document selector and no
 * second uploader — documents were chosen in the previous step.
 */
export const RouterGoalStep: React.FC<RouterGoalStepProps> = ({
  workflowType,
  onContextSubmit,
  onSkip,
  initialValue
}) => {
  const [goal, setGoal] = useState(initialValue || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Hold the callback in a ref so a new function identity from the parent
  // doesn't re-fire the submit effect on every render.
  const submitRef = useRef(onContextSubmit);
  submitRef.current = onContextSubmit;

  useEffect(() => {
    submitRef.current(goal, [], [], []);
  }, [goal]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const examples = GOAL_EXAMPLES[workflowType] || [];

  const handleSkip = () => {
    setGoal('');
    submitRef.current('', [], [], []);
    onSkip();
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          What are you looking to do with these documents?
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          Describe your goal in plain language — it's used to pick the right specialized
          analysis for your documents.
        </p>
      </div>

      <textarea
        ref={textareaRef}
        value={goal}
        onChange={(e) => setGoal(e.target.value)}
        placeholder="e.g. Compare the renewal to the expiring policy and tell me what got worse"
        rows={5}
        className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg
                   focus:outline-none focus:ring-2 focus:ring-primary
                   dark:bg-gray-700 dark:text-white resize-vertical"
      />

      {examples.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Or start from an example
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setGoal(example)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  goal === example
                    ? 'bg-primary text-white border-primary'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-primary hover:text-primary dark:hover:text-white'
                }`}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleSkip}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline underline-offset-2 transition-colors"
        >
          Skip — route automatically from the documents
        </button>
      </div>
    </div>
  );
};

export default RouterGoalStep;
